import React from 'react';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import type { Group } from '../../shared/types';

/**
 * Formats member_count with thousand separators (full number, no K/M).
 * e.g. 37800 → "37.800", 1500000 → "1.500.000"
 */
export function formatMemberCount(count: number): string {
  return count.toLocaleString('vi-VN');
}

/** Format a price in VND with thousand separators, e.g. 1500000 → "1.500.000" */
export function formatPrice(amount: number): string {
  return amount.toLocaleString('vi-VN');
}

interface GroupRowProps {
  group: Group;
}

/**
 * GroupRow — displays a single group as a card row (used on mobile).
 * Requirements: 5.3, 5.4
 */
export const GroupRow: React.FC<GroupRowProps> = ({ group }) => {
  const { name, link, member_count } = group;
  const estimatedPrice = member_count * 150;

  const nameEl = link ? (
    <Link
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      color="primary"
      aria-label={`${name} (mở trong tab mới)`}
      sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.4, wordBreak: 'break-word' }}
    >
      {name}
    </Link>
  ) : (
    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.4, wordBreak: 'break-word' }}>
      {name}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        mb: 1,
        borderRadius: 1,
        bgcolor: 'background.paper',
        boxShadow: 1,
        gap: 0.5,
      }}
    >
      {nameEl}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          👥 {formatMemberCount(member_count)} thành viên
        </Typography>
        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
          ~{formatPrice(estimatedPrice)} đ
        </Typography>
      </Box>
    </Box>
  );
};
